import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { apDebug } from '../apDebug';
import {
  MAX_HOLD_MS,
  SETTLE_GRACE_MS,
  WATCHDOG_RECHECK_MS
} from '../frozenDrop.constants';

interface UseFrozenDropOptions {
  /**
   * True while the drop's API call + refetch are in flight. Pass the combined
   * loading state (e.g. `isLoading || isLoadingUnassignAppointment`).
   */
  isLoading: boolean;
  /**
   * True while a dialog or popover is waiting on the user. The action isn't
   * finished yet, so the watchdog holds off instead of clearing the preview
   * mid-decision.
   */
  holdWhileOpen?: boolean;
  /**
   * Optional extra cleanup run whenever the preview clears (e.g. removing a
   * `data-drag-active` attribute).
   */
  onRelease?: () => void;
  /**
   * Also clear a freeze nobody reported once loading settles, instead of leaving
   * it to the `MAX_HOLD_MS` watchdog.
   *
   * Only safe in a view where one drop can be in flight at a time — there, a
   * `true → false` edge can only be *this* drop's, so it means the same thing a
   * terminal callback would. The Roster and Timeline views qualify: their
   * `LoadingOverlay` covers the whole board for the duration of a drop, so the
   * user cannot start a second one.
   *
   * Leave it off wherever the board stays usable during a drop (the Cards view):
   * the edge is then ambiguous, and clearing on another drop's settle would snap
   * a preview that is still in flight back to its source — a common case traded
   * for a rare one.
   */
  releaseOnSettle?: boolean;
}

/**
 * Handle for one freeze. Both releases are keyed on this freeze: they no-op
 * once a newer drop has taken over the preview, so a late callback from an
 * earlier operation can't tear down the current drag's frame.
 */
export interface FrozenDropHandle {
  /** Monotonic freeze id, so back-to-back drops are told apart. */
  id: number;
  /**
   * Reports this freeze's operation as finished. The preview clears once
   * loading settles, so it covers the mutation response *and* the refetch that
   * follows it — the card lands at its final spot at full opacity while the
   * preview clears simultaneously. Call from success/completion callbacks.
   */
  releaseWhenSettled: () => void;
  /**
   * Clears the preview immediately. Call from cancel/error paths, where the
   * optimistic state is reverted and there is no incoming data to wait for.
   */
  releaseNow: () => void;
}

interface UseFrozenDropReturn<T> {
  /** Freezes the drop frame and takes ownership of the preview. Call on drop. */
  freeze: (value: T) => FrozenDropHandle;
  /**
   * Clears the preview whoever owns it — the one release not keyed on a handle.
   * For teardown that outlives a single operation (unmount, switching
   * perspective); anything scoped to one drop belongs on that drop's handle.
   */
  releaseAnyOwner: () => void;
  /**
   * Stable ref to the latest `releaseAnyOwner`, so effects can call it without
   * taking it as a dependency.
   */
  releaseAnyOwnerRef: RefObject<() => void>;
}

/**
 * Shared lifecycle for the "frozen drop" preview used by the Pragmatic
 * Timeline / Cards / Roster views: after a drop a copy of the card is held at
 * its target position (and the source card stays faded) until the plan/unassign
 * call + refetch settle, replacing the vanished native drag ghost.
 *
 * The freeze is released by the operation that created it, keyed on its handle
 * — never by the loading state alone. Loading is global to the view, so a
 * `true → false` edge says only that *something* finished, not which drop: with
 * per-column loading the board can hold several operations at once, and the
 * edge either fires for the wrong one (clearing a preview that is still in
 * flight) or not at all (stranding a card copy over the board). It only decides
 * *when* an already-finished freeze clears.
 *
 * Presentation only; no source-of-truth change.
 *
 * Storage-agnostic: takes the setter rather than the store it writes to, so the
 * Cards/Roster views can pass the Zustand store's setter while the Timeline
 * keeps writing its own (legacy) Recoil atom. `setFrozenDrop` must have a stable
 * identity — it is a dependency of `freeze`.
 */
export const useFrozenDrop = <T>(
  setFrozenDrop: (value: T | null) => void,
  {
    isLoading,
    holdWhileOpen = false,
    onRelease,
    releaseOnSettle = false
  }: UseFrozenDropOptions
): UseFrozenDropReturn<T> => {
  const freezeIdRef = useRef(0);
  /** Freeze that currently owns the preview; null when nothing is frozen. */
  const ownerRef = useRef<number | null>(null);
  /** The owner reported it is finished and is waiting for loading to settle. */
  const pendingReleaseRef = useRef(false);
  /**
   * Loading has been true since this freeze took the preview, so a return to
   * idle is a settle rather than the gap before the request went out. Only read
   * when `releaseOnSettle` is on.
   */
  const sawLoadingRef = useRef(false);

  // Latest committed values, for the timers to read.
  const isLoadingRef = useRef(isLoading);
  const holdWhileOpenRef = useRef(holdWhileOpen);

  const onReleaseRef = useRef(onRelease);
  onReleaseRef.current = onRelease;

  const flushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (flushTimeoutRef.current !== null) {
      clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = null;
    }
    if (watchdogRef.current !== null) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  }, []);

  const clear = useCallback(() => {
    ownerRef.current = null;
    pendingReleaseRef.current = false;
    clearTimers();
    setFrozenDrop(null);
    onReleaseRef.current?.();
  }, [clearTimers, setFrozenDrop]);

  const scheduleFlush = useCallback(() => {
    if (flushTimeoutRef.current !== null) {
      clearTimeout(flushTimeoutRef.current);
    }
    flushTimeoutRef.current = setTimeout(() => {
      flushTimeoutRef.current = null;
      if (!pendingReleaseRef.current) return;
      // Loading picked up again (usually the GET the response triggered), or a
      // dialog is waiting on the user. Going idle again re-arms this.
      if (isLoadingRef.current || holdWhileOpenRef.current) return;
      clear();
    }, SETTLE_GRACE_MS);
  }, [clear]);

  const freeze = useCallback(
    (value: T): FrozenDropHandle => {
      freezeIdRef.current += 1;
      const id = freezeIdRef.current;

      ownerRef.current = id;
      // A pending release belongs to the freeze this one replaces; it must not
      // clear this preview.
      pendingReleaseRef.current = false;
      // Seeded from the current state, not false: a drop made while something is
      // already in flight would otherwise never see a rising edge of its own, so
      // the settle that follows would not count as one.
      sawLoadingRef.current = isLoadingRef.current;
      clearTimers();

      const watchdogTick = () => {
        // Only a freeze nobody released is stranded. While something is in
        // flight or a dialog is waiting on the user the operation is still
        // alive, however slow it is, and its terminal callback is what should
        // clear this — firing here would drop the preview and un-fade the
        // source card mid-flight. Look again shortly instead, so recovery
        // follows the hold rather than waiting out another full cap.
        if (holdWhileOpenRef.current || isLoadingRef.current) {
          watchdogRef.current = setTimeout(watchdogTick, WATCHDOG_RECHECK_MS);
          return;
        }
        watchdogRef.current = null;
        // A watchdog firing means a drop's terminal callback never released its
        // freeze — worth knowing when diagnosing a board that stopped reacting.
        apDebug('frozenDrop.watchdogCleared', { freezeId: id });
        clear();
      };
      watchdogRef.current = setTimeout(watchdogTick, MAX_HOLD_MS);

      setFrozenDrop(value);

      const isOwner = () => ownerRef.current === id;

      return {
        id,
        releaseWhenSettled: () => {
          if (!isOwner()) return;
          pendingReleaseRef.current = true;
          scheduleFlush();
        },
        releaseNow: () => {
          if (!isOwner()) return;
          clear();
        }
      };
    },
    [clear, clearTimers, scheduleFlush, setFrozenDrop]
  );

  // Stable ref so effects can call the latest release without a dependency.
  const releaseAnyOwnerRef = useRef(clear);
  releaseAnyOwnerRef.current = clear;

  useEffect(() => {
    isLoadingRef.current = isLoading;
    holdWhileOpenRef.current = holdWhileOpen;

    if (ownerRef.current === null) return;
    if (isLoading) {
      sawLoadingRef.current = true;
      return;
    }
    if (holdWhileOpen) return;

    // Nothing reported this freeze, but its drop has been in flight and is now
    // idle. In a single-drop view that edge is this drop's own, so treat it as
    // the report that never came rather than leaving the preview to the 30s
    // watchdog. Routed through the same grace period as a real report, so it
    // still can't clear inside the gap between the response and its refetch.
    if (!pendingReleaseRef.current) {
      if (!releaseOnSettle || !sawLoadingRef.current) return;
      pendingReleaseRef.current = true;
    }

    scheduleFlush();
  }, [holdWhileOpen, isLoading, releaseOnSettle, scheduleFlush]);

  // Release on unmount so the shared state can't flash a stale preview on
  // remount.
  useEffect(() => {
    return () => {
      releaseAnyOwnerRef.current();
    };
  }, []);

  return { freeze, releaseAnyOwner: clear, releaseAnyOwnerRef };
};
